import {fromJson} from '../../../../util/runs';
import {detectZeroLengthHistories} from '../../alerts';
import {BucketedLine} from '../../types';
import {QueryVars} from './queryVars';
import {
  BucketedQueryRunsDeltas,
  GroupedBucketedQueryRunWithGroupedRunConnection,
} from './types';

export function applyDelta({
  currentRunsById,
  delta,
  order,
  specs,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: BucketedQueryRunsDeltas;
  specs: QueryVars['bucketedHistorySpecs'];
}): Record<string, BucketedLine> {
  const newRunsById = delta.reduce(
    (acc, runDelta) => {
      const parsedRun = fromJson(runDelta.run);
      if (parsedRun == null || runDelta.run == null) {
        console.error('Unable to parse run from server in outliers reducer');
        return acc;
      }

      detectZeroLengthHistories({
        histories: runDelta.run?.bucketedHistory ?? [],
        id: runDelta.run.id,
        displayName: runDelta.run.displayName ?? '',
        specs,
      });

      acc[runDelta.run.id] = {
        ...parsedRun,
        history: runDelta.run?.bucketedHistory ?? [],
      };
      return acc;
    },
    {} as Record<string, BucketedLine>
  );

  /**
   * TODO: THIS IS A HACK - REFACTOR
   * The problem is that we need to display the runs in the plot in the `order` that's returned from the delta. If you construct a JS object by adding the key/value pairs in a specific order, you can get a reliable iteration of those k/v pairs with Object.keys(). However this is an implicit behavior, and easily regressed. Since we return the order object _specifically_ to control this UI concern (since we can't reliably recreate the sort order determined by the server on the client as we might not have the proper info in the run to do the comparison) we need to find a way pipe this through so we can defer to it in the UI to display the runs.
   */
  const runsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    runsById[rId] = newRunsById[rId] ?? currentRunsById[rId];
  });

  return runsById;
}

// Narrowed deltas type specifically for the GroupedRunConnection branch so array elements
// are not inferred as any when reducing over union-shaped arrays
export type GroupedBucketedRunDeltas = NonNullable<
  NonNullable<GroupedBucketedQueryRunWithGroupedRunConnection['deltas']>
>['delta'];

export function applyGroupedDelta({
  currentRunsById,
  delta,
  order,
  specs,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: GroupedBucketedRunDeltas;
  specs: QueryVars['bucketedHistorySpecs'];
}) {
  const newRunsById = delta.reduce(
    (acc, runDelta) => {
      if (runDelta.__typename !== 'GroupBucketDiff') {
        console.error('Grouped Bucketed Delta Op returned without valid run');
        return acc;
      }
      if (!runDelta.groupedRun) {
        console.error('Grouped Bucketed Delta Op returned without valid run');
        return acc;
      }
      // RunFromGraphQL doesn't allow null for createdAt
      const groupedRun = {
        ...runDelta.groupedRun,
        createdAt: runDelta.groupedRun.createdAt ?? undefined,
      };

      const parsedRun = fromJson(groupedRun);
      if (parsedRun == null) {
        console.error('Unable to parse run from server in outliers reducer');
        return acc;
      }

      detectZeroLengthHistories({
        histories: runDelta.groupedRun?.bucketedHistory ?? [],
        id: runDelta.groupedRun.id,
        displayName: runDelta.groupedRun.displayName ?? '',
        specs,
      });
      acc[runDelta.groupedRun.id] = {
        ...parsedRun,
        history: runDelta.groupedRun?.bucketedHistory ?? [],
      };
      return acc;
    },
    {} as Record<string, BucketedLine>
  );

  /**
   * TODO: THIS IS A HACK - REFACTOR
   * The problem is that we need to display the runs in the plot in the `order` that's returned from the delta. If you construct a JS object by adding the key/value pairs in a specific order, you can get a reliable iteration of those k/v pairs with Object.keys(). However this is an implicit behavior, and easily regressed. Since we return the order object _specifically_ to control this UI concern (since we can't reliably recreate the sort order determined by the server on the client as we might not have the proper info in the run to do the comparison) we need to find a way pipe this through so we can defer to it in the UI to display the runs.
   */
  const runsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    runsById[rId] = newRunsById[rId] ?? currentRunsById[rId];
  });

  return runsById;
}
